/**
 * В данном файле содержатся утилиты, предназначенные для расширения логики формы
 * React Final Form. На данный момент их названия не отражают того какие действия они выполняют и для чего предназначены, а только указывают на некоторые случаи их применения. В связи с этим лучше руководствоваться комментариями. В будущем неплохо бы изменить нейминг на более удобный
 */
import type { AnyObject, FieldInputProps, FieldRenderProps } from 'react-final-form';

import type { TFormActionElement, TFormValues } from '../models';

export class StaticActions {
    /**
     * Вызывает коллбэк onBlur из FieldInputProps. Используется, когда требуется вручную обозначить, что фокус должен быть убран с выбранного поля (например, когда оно корректно заполнено).
     */
    static onBlur =
        <T>(input: FieldInputProps<T>) =>
        () =>
            input.onBlur();

    /**
     * Вызывает асинхронный коллбэк onFocus из FieldInputProps. Используется, когда требуется вручную обозначить, что фокус должен быть перемещен на выбранное поле. Асинхроный вызов требуется, чтобы удостовериться, что onFocus вызван после onBlur во избежание некорректного поведения формы.
     */
    static onFocus =
        <T>(input: FieldInputProps<T>) =>
        () =>
            setTimeout(() => input.onFocus(), 0);

    /**
     * Вызывает callback onChange, переданный в FieldInputProps,
     * также в случае если значение оказалось типа boolean, будет
     * вызван экшен onBlur, этим предполагается запустить процесс
     * валидации для чекбоксов, если таковая у них присутствует.
     * Также, если значение true и при этом передана meta, для смены
     * фокуса будет вызван экшен onComplete
     */
    static onChange =
        <T>(
            input: FieldInputProps<T>,
            /** Вызывается, если value === true */
            meta?: FieldRenderProps<unknown, HTMLElement>['meta']
        ) =>
        (value: T) => {
            input.onChange(value);

            // Для чекбоксов - форсить валидацию по блуру
            if (typeof value === 'boolean') {
                StaticActions.onBlur(input)();
                if (value && meta) {
                    StaticActions.onComplete({ input, meta });
                }
            }
        };

    /**
     * Будет инициирован перевод фокуса путем вызова коллбэка
     * onComplete из field.meta.data; если же у поля нет способности
     * к переводу фокуса, будет просто вызван экшен onBlur
     */
    static onComplete = (field: FieldRenderProps<unknown>) => {
        StaticActions.onBlur(field.input)();
        if (!field.meta.data?.dispatchesFocus) return;
        field.meta.data?.onComplete?.(field.input.name);
    };

    /**
     * Используется для полей, где присутствуют suggestions (выпадающие списки с вариантами автозаполнения).
     * Не стоит сочетать с экшеном onBlur: вызов onBlur до onSelect может привести к непредсказуемому поведению,
     * а вызов onBlur после не имеет смысла, так как он все равно вызывается внутри экшена onComplete
     */
    static onSelect = (field: FieldRenderProps<unknown>) => (value: string) => {
        StaticActions.onChange(field.input)(value);
        StaticActions.onComplete(field);
    };

    /**
     * Перевод фокуса на выбранное поле при условии, что в field.meta.data есть ref для него
     */
    static onCancel = (field: FieldRenderProps<unknown>) => () => {
        const { current } = StaticActions.getRef(field.meta);

        if (current) {
            current.focus();
        }
    };

    /**
     * Достает ref поля из fieldInputProps.meta.data.ref
     */
    static getRef = (meta: FieldRenderProps<unknown, HTMLElement>['meta']) => meta.data?.ref ?? {};

    /**
     * Достает коллбэк updateAction из fieldInputProps.meta.data
     */
    static updateAction = (
        meta: FieldRenderProps<unknown, HTMLElement>['meta'],
        actions: Record<string, TFormActionElement>
    ) => meta.data?.onUpdateFormAction?.(actions);

    /**
     * Используется вместе с компонентом Fields. Преобразует массив имен в объект имен
     */
    static getNames = <T extends string>(
        names: Record<T, string> | Array<Partial<Record<T, string>>>
    ): Record<T, string> => (Array.isArray(names) ? Object.assign({}, ...names) : names);

    /**
     * Получает значение по пути к объекту.
     * Если разрешенное значение `undefined`, вместо него возвращается значение `defaultValue`
     * @example
     * let object = { 'a': [{ 'b': { 'c': 3 } }] };
     * getValue(object, 'a[0].b.c'); // => 3
     * */
    static getValue = <T>(
        /** Контекст формы */
        values: TFormValues,
        /** Путь до значения в объекте */
        path?: string,
        /** Значение по умолчанию */
        defaultValue?: T
    ) => {
        const travel = (regexp: RegExp) =>
            String.prototype.split
                .call(path, regexp)
                .filter(Boolean)
                .reduce(
                    (res: AnyObject, key: string) =>
                        res !== null && res !== undefined ? res[key] : res,
                    values
                );

        const result =
            // последовательности из запятых, точек и/или квадратных скобок
            travel(/[,[\]]+/) ||
            // последовательности из запятых и/или квадратных скобок
            travel(/[,.[\]]+/);

        return result === undefined || result === values ? defaultValue : (result as T);
    };
}
